// Where a coin's price history comes from, in one table.
//
// This lived in two places that could not see each other: the TMA held
// CoinGecko ids keyed by token symbol, and the API held CoinGecko id →
// Binance symbol keyed by a plain string. Only the first was exhaustive, so
// adding Dogecoin and XRP compiled cleanly while their charts came up empty —
// unmapped ids fall through to the CoinGecko candle endpoint, and a new coin
// has no cached chart to fall back on when that is rate-limited.
//
// `Record<AnyTokenSymbol, …>` makes the row mandatory on both sides instead.

import type { AnyTokenSymbol } from './types';

export interface MarketIds {
  /** CoinGecko id — market cap, volume, and the candle fallback. */
  coingeckoId: string;
  /**
   * Binance symbol for the PRIMARY candle source, or null when the coin has
   * no pair there.
   *
   * Null is a real answer, not a gap to fill in later: charting a coin on
   * another coin's book is worse than showing no chart, which is why the
   * lookup has never had a default. USDT and USDC quote against USD directly;
   * everything else uses the USDT pair, the same convention as lib/prices.ts.
   */
  binanceSymbol: string | null;
}

export const MARKET_IDS: Record<AnyTokenSymbol, MarketIds> = {
  USDT: { coingeckoId: 'tether', binanceSymbol: 'USDTUSD' },
  USDC: { coingeckoId: 'usd-coin', binanceSymbol: 'USDCUSD' },
  ETH: { coingeckoId: 'ethereum', binanceSymbol: 'ETHUSDT' },
  BTC: { coingeckoId: 'bitcoin', binanceSymbol: 'BTCUSDT' },
  TRX: { coingeckoId: 'tron', binanceSymbol: 'TRXUSDT' },
  BNB: { coingeckoId: 'binancecoin', binanceSymbol: 'BNBUSDT' },
  SOL: { coingeckoId: 'solana', binanceSymbol: 'SOLUSDT' },
  HYPE: { coingeckoId: 'hyperliquid', binanceSymbol: 'HYPEUSDT' },
  // June 2026: the coin rebranded Toncoin → Gram. The chain is still TON.
  // CoinGecko kept the id `the-open-network`; Binance moved the book to
  // GRAMUSDT. TONUSDT still answers, at a different price, and is not this
  // market.
  TON: { coingeckoId: 'the-open-network', binanceSymbol: 'GRAMUSDT' },
  ZEC: { coingeckoId: 'zcash', binanceSymbol: 'ZECUSDT' },
  XMR: { coingeckoId: 'monero', binanceSymbol: 'XMRUSDT' },
  DOGE: { coingeckoId: 'dogecoin', binanceSymbol: 'DOGEUSDT' },
  XRP: { coingeckoId: 'ripple', binanceSymbol: 'XRPUSDT' },
};

/** CoinGecko id for a coin — what the market endpoints are keyed by. */
export function coingeckoId(symbol: AnyTokenSymbol): string {
  return MARKET_IDS[symbol].coingeckoId;
}

/**
 * CoinGecko id → Binance symbol, derived rather than written out a second
 * time. The API receives an id on the wire and has nothing else to look up by.
 */
export const BINANCE_SYMBOL_BY_CG_ID: Record<string, string> = Object.fromEntries(
  Object.values(MARKET_IDS)
    .filter((m): m is MarketIds & { binanceSymbol: string } => m.binanceSymbol !== null)
    .map((m) => [m.coingeckoId, m.binanceSymbol]),
);
